import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import NoDataComponent from "./NoData";

// Define option type
export type Option = {
  value: string;
  label: string;
};

// Extend SelectProps to accept onChange as a prop
export interface SelectProps {
  options: Option[];
  defaultValue?: Option;
  initialValues?: Option; // kept for compatibility (unused)
  className?: string;
  styles?: any; // Optional custom styles if needed
  onChange?: (value: Option | null) => void; // handle changes externally

  /** NEW (optional): minimum characters before showing results */
  minSearchChars?: number; // default 0 -> behaves exactly as before
  /** NEW (optional): enable/disable searching */
  isSearchable?: boolean; // default true
}

const normalize = (s: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const CommonSelect: React.FC<SelectProps> = ({
  options,
  defaultValue,
  className,
  styles,
  onChange, // onChange prop from parent
  minSearchChars = 0,         // NEW (0 keeps old behavior)
  isSearchable = true,        // NEW
}) => {
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(defaultValue);
  const [inputValue, setInputValue] = useState<string>(""); // for search box
  const { t } = useTranslation();

  const handleChange = (option: Option | null) => {
    setSelectedOption(option || undefined);
    if (onChange) onChange(option);
  };

  // Set initial value based on defaultValue
  useEffect(() => {
    setSelectedOption(defaultValue || undefined);
  }, [defaultValue]);

  // Custom filter that:
  // - gates until input length >= minSearchChars
  // - matches label or value, accent/case-insensitive
  const filterOption = useMemo(() => {
    return (candidate: { label: string; value: string }, rawInput: string) => {
      if (!isSearchable) return true;
      const q = normalize(rawInput);
      if (q.length < minSearchChars) return minSearchChars === 0; // show all when 0, else nothing
      if (!q) return true;
      return (
        normalize(candidate.label).includes(q) ||
        normalize(String(candidate.value)).includes(q)
      );
    };
  }, [minSearchChars, isSearchable]);

  return (
    <Select
      classNamePrefix="react-select"
      className={className}
      styles={styles}
      options={options}
      value={selectedOption}
      onChange={handleChange}
      placeholder={t("select")}
      isSearchable={isSearchable}
      filterOption={filterOption}
      onInputChange={(val) => {
        setInputValue(val || "");
        return val;
      }}
      noOptionsMessage={({ inputValue: iv }) =>
        isSearchable && normalize(iv).length < minSearchChars && minSearchChars > 0 ? (
          <div style={{ padding: 8, color: "#6b7280", fontSize: 13 }}>
            {t("type_min_chars_to_search", {
              count: minSearchChars,
              defaultValue: `Type at least ${minSearchChars} characters to search`,
            })}
          </div>
        ) : (
          <NoDataComponent />
        )
      }
    />
  );
};

export default CommonSelect;
